import { useMutation } from '@apollo/client';
import type { DeleteOversightAssessmentsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteOversightAssessmentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteOversightAssessmentsTRPC } from './useDeleteOversightAssessmentsTRPC';

export const useDeleteOversightAssessments = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteOversightAssessmentsGraphQL, graphqlState] = useMutation(
    DeleteOversightAssessmentsDocument,
    {
      update: (cache) => {
        evictField(cache, 'oversight_assessment');
        evictField(cache, 'oversight_assessment_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteOversightAssessmentsTRPC();

  const deleteOversightAssessments = async (
    ids: string[]
  ): Promise<DeleteOversightAssessmentsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteOversightAssessments(ids);
    }

    const result = await deleteOversightAssessmentsGraphQL({
      variables: { Ids: ids },
    });
    if (!result.data) {
      throw new Error('Failed to delete oversight assessments');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      deleteOversightAssessments,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteOversightAssessments,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
